import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Grid2,
  Chip,
} from "@mui/material";
import { collection, addDoc, doc, updateDoc } from "firebase/firestore";
import { db } from "../../../firestore-DB-config/firebase-config";
import { SelectChangeEvent } from "@mui/material"; // Import for SelectChangeEvent
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import ImageUploader from "../../../components/image-uploader/image";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import HomeIcon from "@mui/icons-material/Home";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

const AddProperty: React.FC = () => {
  const [formData, setFormData] = useState({
    imageUrls: [] as string[],
    price: "",
    title: "",
    description: "",
    address: "",
    beds: 0,
    baths: 0,
    label: "",
    labelColor: "primary", // default value
    type: "",
    roomDetails: "",
    postedBy: "",
    postedDate: "",
    landmark: "",
    floor: "",
    balcony: "",
    facing: "",
    parking: "",
    gatedSecurity: "",
    area: "",
    age: "",
    furnished: "",
    amenities: [] as string[],
  });

  const [propertyId, setPropertyId] = useState<string | null>(""); // State to store Firestore property ID
  const [isUploading, setIsUploading] = useState<boolean>(false); // To handle upload state
  const [uploadCompleted, setUploadCompleted] = useState<boolean>(false); // To track upload completion

  // State to handle the current amenity input
  const [amenityInput, setAmenityInput] = useState<string>("");

  const handleAmenityKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && amenityInput.trim() !== "") {
      event.preventDefault();

      setFormData((prevData) => ({
        ...prevData,
        amenities: [...prevData.amenities, amenityInput.trim()], // Add the amenity to the array
      }));

      setAmenityInput(""); // Clear the input field
    }
  };

  // Function to remove an amenity
  const handleAmenityDelete = (amenityToDelete: string) => {
    setFormData((prevData) => ({
      ...prevData,
      amenities: prevData.amenities.filter(
        (amenity) => amenity !== amenityToDelete
      ),
    }));
  };

  // Separate event handler for input fields
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Separate event handler for Select component
  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Callback when image upload is successful
  const handleImageUploadSuccess = (imageUrls: string[]) => {
    console.log("Image uploaded: ", imageUrls);
    // Update Firestore with the image URL
    if (propertyId) {
      const propertyRef = doc(db, "properties", propertyId);
      updateDoc(propertyRef, { imageUrls }) // Update the image field in Firestore
        .then(() => {
          console.log("Property updated with image URL");
          setUploadCompleted(true); // Indicate the upload is completed
        })
        .catch((error) => {
          console.error("Error updating property with image URL: ", error);
        });
    }
  };

  const resetForm = () => {
    setFormData({
      imageUrls: [] as string[],
      price: "",
      title: "",
      description: "",
      address: "",
      beds: 0,
      baths: 0,
      label: "",
      labelColor: "primary", // default value
      type: "",
      roomDetails: "",
      postedBy: "",
      postedDate: "",
      landmark: "",
      floor: "",
      balcony: "",
      facing: "",
      parking: "",
      gatedSecurity: "",
      area: "",
      age: "",
      furnished: "",
      amenities: [] as string[],
    });
    setPropertyId(null); // Reset the blogId to show the form again
    setUploadCompleted(false); // Reset the upload completion status
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Convert description to HTML format with <p> tags
    const formattedDescription = formData.description
      .split("\n")
      .map((para) => `<p>${para}</p>`)
      .join(""); // Combine paragraphs into a single string

    // Create the final form data, replacing the description with the formatted one
    const propertyData = {
      ...formData,
      beds: parseInt(formData.beds as any),
      baths: parseInt(formData.baths as any),
      description: formattedDescription, // Store the formatted description
    };

    // Add the property to Firestore first, without the image
    addDoc(collection(db, "properties"), propertyData)
      .then((docRef) => {
        setPropertyId(docRef.id); // Store the property ID from Firestore
        console.log("Property added successfully with ID:", docRef.id);
        setIsUploading(true); // Enable image uploader
      })
      .catch((error) => {
        console.error("Error adding property: ", error);
      });
  };

  return (
    <>
      <Header />

      {/* Conditionally render the form or the image uploader */}
      {!propertyId ? (
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            maxWidth: 600,
            margin: "auto",
            my: 4,
            p: 3,
            boxShadow: 2,
            borderRadius: 2,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            align="center"
            fontWeight="bold"
            sx={{ color: "#333" }}
          >
            Add New Property
          </Typography>
          <Box display="flex" flexDirection="column" gap={3}>
            {/* Existing fields */}
            <TextField
              label="Title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
              sx={{ bgcolor: "#fff" }}
            />
            <TextField
              label="Address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
              sx={{ bgcolor: "#fff" }}
            />
            <TextField
              label="Landmark"
              name="landmark"
              value={formData.landmark}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
              sx={{ bgcolor: "#fff" }}
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={4}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              label="Price"
              name="price"
              value={formData.price}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
              sx={{ bgcolor: "#fff" }}
            />
            <Grid2 container spacing={2}>
              <Grid2 item xs={6} {...({} as any)}>
                <TextField
                  label="Beds"
                  name="beds"
                  type="number"
                  value={formData.beds}
                  onChange={handleChange}
                  fullWidth
                  required
                  variant="outlined"
                  sx={{ bgcolor: "#fff" }}
                />
              </Grid2>
              <Grid2 item xs={6} {...({} as any)}>
                <TextField
                  label="Baths"
                  name="baths"
                  type="number"
                  value={formData.baths}
                  onChange={handleChange}
                  fullWidth
                  required
                  variant="outlined"
                  sx={{ bgcolor: "#fff" }}
                />
              </Grid2>
            </Grid2>

            {/* New fields */}
            <TextField
              label="Room Details"
              name="roomDetails"
              value={formData.roomDetails}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
              sx={{ bgcolor: "#fff" }}
            />
            <TextField
              label="Posted By"
              name="postedBy"
              value={formData.postedBy}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
              sx={{ bgcolor: "#fff" }}
            />
            <TextField
              label="Posted Date"
              name="postedDate"
              type="date"
              value={formData.postedDate}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
              sx={{ bgcolor: "#fff" }}
              InputLabelProps={{ shrink: true }} // This ensures the label doesn't overlap the date picker
            />

            <Grid2 container spacing={2}>
              <Grid2 item xs={6} {...({} as any)}>
                <TextField
                  label="Floor"
                  name="floor"
                  type="number"
                  value={formData.floor}
                  onChange={handleChange}
                  fullWidth
                  required
                  variant="outlined"
                  sx={{ bgcolor: "#fff" }}
                />
              </Grid2>
              <Grid2 item xs={6} {...({} as any)}>
                <TextField
                  label="Balcony"
                  name="balcony"
                  type="number"
                  value={formData.balcony}
                  onChange={handleChange}
                  fullWidth
                  required
                  variant="outlined"
                  sx={{ bgcolor: "#fff" }}
                />
              </Grid2>
            </Grid2>
            <TextField
              label="Facing"
              name="facing"
              value={formData.facing}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
              sx={{ bgcolor: "#fff" }}
            />
            <TextField
              label="Parking"
              name="parking"
              value={formData.parking}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
              sx={{ bgcolor: "#fff" }}
            />
            <TextField
              label="Gated Security"
              name="gatedSecurity"
              value={formData.gatedSecurity}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
              sx={{ bgcolor: "#fff" }}
            />
            <TextField
              label="Area"
              name="area"
              value={formData.area}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
              sx={{ bgcolor: "#fff" }}
            />
            <TextField
              label="Age of Property"
              name="age"
              value={formData.age}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
              sx={{ bgcolor: "#fff" }}
            />
            <TextField
              label="Furnished"
              name="furnished"
              value={formData.furnished}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
              sx={{ bgcolor: "#fff" }}
            />

            <FormControl fullWidth variant="outlined" sx={{ bgcolor: "#fff" }}>
              <TextField
                label="Add Amenities"
                value={amenityInput}
                onChange={(e) => setAmenityInput(e.target.value)}
                onKeyDown={handleAmenityKeyPress}
                variant="outlined"
                fullWidth
                sx={{ bgcolor: "#fff" }}
              />
              <Button
                onClick={() => {
                  if (amenityInput.trim() !== "") {
                    setFormData((prevData) => ({
                      ...prevData,
                      amenities: [...prevData.amenities, amenityInput.trim()],
                    }));
                    setAmenityInput("");
                  }
                }}
                variant="contained"
                sx={{ mt: 1 }}
              >
                Add Amenity
              </Button>

              <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                {formData.amenities.map((amenity, index) => (
                  <Chip
                    key={index}
                    label={amenity}
                    onDelete={() => handleAmenityDelete(amenity)}
                    color="primary"
                  />
                ))}
              </Box>
            </FormControl>

            {/* Dropdown for Property Label */}
            <FormControl
              fullWidth
              required
              variant="outlined"
              sx={{ bgcolor: "#fff" }}
            >
              <InputLabel id="label-select">Label</InputLabel>
              <Select
                labelId="label-select"
                name="label"
                value={formData.label}
                onChange={handleSelectChange}
                label="Label"
              >
                <MenuItem value="Popular">Popular</MenuItem>
                <MenuItem value="Newest">Newest</MenuItem>
                <MenuItem value="Discounted">Most Discounted</MenuItem>
              </Select>
            </FormControl>

            {/* Dropdown for Property Type */}
            <FormControl
              fullWidth
              required
              variant="outlined"
              sx={{ bgcolor: "#fff" }}
            >
              <InputLabel id="property-select">Property Type</InputLabel>
              <Select
                labelId="property-select"
                name="type"
                value={formData.type}
                onChange={handleSelectChange}
                label="Property Type"
              >
                <MenuItem value="For Sell">For Sell</MenuItem>
                <MenuItem value="For Rent">For Rent</MenuItem>
              </Select>
            </FormControl>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 2 }}
            >
              Submit
            </Button>
          </Box>
        </Box>
      ) : (
        // Show the ImageUploader after form submission
        <Box
          sx={{
            display: "flex", // Flexbox for centering
            flexDirection: "column",
            alignItems: "center", // Center vertically
            minHeight: "70vh", // Occupy full viewport height
            maxWidth: 700,
            margin: "auto",
            p: 3,
            boxShadow: 2,
            borderRadius: 2,
            textAlign: "center",
            my: 4,
          }}
        >
          <Typography variant="h6" mb={4}>
            Upload Images for Property
          </Typography>
          <ImageUploader
            folderPath="properties" // Main folder where blog images will be stored
            uniqueId={propertyId} // Use the blog ID as the subfolder name
            onUploadSuccess={handleImageUploadSuccess}
          />

          {uploadCompleted && (
            <Button
              variant="contained"
              sx={{ mt: 3 }}
              onClick={resetForm} // Reset the form after upload is done
            >
              Add Another Property
            </Button>
          )}
        </Box>
      )}

      <Footer />
    </>
  );
};

export default AddProperty;
